import { useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAllAdminProducts, useUpdateProduct } from "@/hooks/useProducts";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { ImageIcon, Upload, CheckCircle, XCircle, RefreshCw } from "lucide-react";

const SUPABASE_URL = import.meta.env.VITE_SUPABASE_URL;

async function migrateImage(imageUrl: string, storagePath: string): Promise<string | null> {
  try {
    const { data, error } = await supabase.functions.invoke("migrate-image", {
      body: { imageUrl, storagePath },
    });
    if (error) {
      console.error("Migration error:", error);
      return null;
    }
    if (data?.success) return data.publicUrl;
    console.error("Migration failed:", data?.error);
    return null;
  } catch (e) {
    console.error("Migration error:", e);
    return null;
  }
}

function extractFileName(url: string): string {
  const path = new URL(url).pathname;
  return path.split("/").pop() || `image-${Date.now()}.jpg`;
}

type MigrationStatus = "idle" | "running" | "done";

interface MigrationResult {
  productId: number;
  productName: string;
  status: "success" | "partial" | "failed";
  originalCount: number;
  migratedCount: number;
}

const AdminImageMigration = () => {
  const { data: products, isLoading } = useAllAdminProducts();
  const updateProduct = useUpdateProduct();
  const [status, setStatus] = useState<MigrationStatus>("idle");
  const [progress, setProgress] = useState(0);
  const [total, setTotal] = useState(0);
  const [results, setResults] = useState<MigrationResult[]>([]);
  const [currentProduct, setCurrentProduct] = useState("");

  const productsWithExternalImages = products?.filter((p) => {
    if (!p.images) return false;
    return p.images.split(",").some((img) => {
      const trimmed = img.trim();
      return trimmed.startsWith("http://") || trimmed.startsWith("https://");
    });
  }) || [];

  const alreadyMigrated = products?.filter((p) => {
    if (!p.images) return false;
    const imgs = p.images.split(",").map((s) => s.trim()).filter(Boolean);
    return imgs.length > 0 && imgs.every((img) => img.includes(SUPABASE_URL));
  }).length || 0;

  const handleMigrate = async () => {
    if (!productsWithExternalImages.length) return;

    setStatus("running");
    setProgress(0);
    setTotal(productsWithExternalImages.length);
    setResults([]);

    for (let i = 0; i < productsWithExternalImages.length; i++) {
      const product = productsWithExternalImages[i];
      setCurrentProduct(product.name);
      setProgress(i);

      const imageUrls = product.images!.split(",").map((s) => s.trim()).filter(Boolean);
      const newUrls: string[] = [];
      let migratedCount = 0;

      for (const url of imageUrls) {
        // Skip if already in our storage
        if (url.includes(SUPABASE_URL)) {
          newUrls.push(url);
          migratedCount++;
          continue;
        }

        const fileName = extractFileName(url);
        const storagePath = `${product.id}/${fileName}`;
        const newUrl = await migrateImage(url, storagePath);

        if (newUrl) {
          newUrls.push(newUrl);
          migratedCount++;
        } else {
          // Keep original URL as fallback
          newUrls.push(url);
        }
      }

      // Update product with new image URLs
      if (migratedCount > 0) {
        try {
          await updateProduct.mutateAsync({
            id: product.id,
            images: newUrls.join(", "),
          });
        } catch (e) {
          console.error("Failed to update product:", e);
        }
      }

      setResults((prev) => [
        ...prev,
        {
          productId: product.id,
          productName: product.name,
          status: migratedCount === imageUrls.length ? "success" : migratedCount > 0 ? "partial" : "failed",
          originalCount: imageUrls.length,
          migratedCount,
        },
      ]);
    }

    setProgress(productsWithExternalImages.length);
    setStatus("done");
    toast.success("Image migration complete!");
  };

  const successCount = results.filter((r) => r.status === "success").length;
  const partialCount = results.filter((r) => r.status === "partial").length;
  const failedCount = results.filter((r) => r.status === "failed").length;

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-display font-bold text-foreground">Image Migration</h1>
        <p className="text-muted-foreground font-body mt-1">
          Migrate product images from external URLs to your storage bucket
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-body text-muted-foreground">External Images</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-2xl font-bold">{isLoading ? "…" : productsWithExternalImages.length}</p>
            <p className="text-xs text-muted-foreground">products need migration</p>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-body text-muted-foreground">Already Migrated</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-2xl font-bold">{isLoading ? "…" : alreadyMigrated}</p>
            <p className="text-xs text-muted-foreground">products using storage</p>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-body text-muted-foreground">Total Products</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-2xl font-bold">{isLoading ? "…" : products?.length || 0}</p>
            <p className="text-xs text-muted-foreground">in database</p>
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <ImageIcon className="w-5 h-5" />
            Migration Tool
          </CardTitle>
          <CardDescription>
            This will download each product image from its current URL and upload it to your storage bucket, then update the product record.
            Images that fail to download will keep their original URL.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {status === "idle" && (
            <Button
              onClick={handleMigrate}
              disabled={!productsWithExternalImages.length || isLoading}
              className="gap-2"
            >
              <Upload className="w-4 h-4" />
              Start Migration ({productsWithExternalImages.length} products)
            </Button>
          )}

          {status === "running" && (
            <div className="space-y-3">
              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                <RefreshCw className="w-4 h-4 animate-spin" />
                Migrating: {currentProduct}
              </div>
              <Progress value={total > 0 ? (progress / total) * 100 : 0} />
              <p className="text-xs text-muted-foreground">
                {progress} / {total} products processed
              </p>
            </div>
          )}

          {status === "done" && (
            <div className="space-y-4">
              <div className="flex gap-3">
                <Badge variant="default" className="gap-1">
                  <CheckCircle className="w-3 h-3" /> {successCount} success
                </Badge>
                {partialCount > 0 && (
                  <Badge variant="secondary" className="gap-1">
                    {partialCount} partial
                  </Badge>
                )}
                {failedCount > 0 && (
                  <Badge variant="destructive" className="gap-1">
                    <XCircle className="w-3 h-3" /> {failedCount} failed
                  </Badge>
                )}
              </div>

              <Button variant="outline" onClick={() => { setStatus("idle"); setResults([]); }}>
                Run Again
              </Button>
            </div>
          )}
        </CardContent>
      </Card>

      {results.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle>Results</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2 max-h-96 overflow-y-auto">
              {results.map((r) => (
                <div key={r.productId} className="flex items-center justify-between py-2 border-b border-border last:border-0">
                  <span className="text-sm font-body truncate flex-1">{r.productName}</span>
                  <div className="flex items-center gap-2">
                    <span className="text-xs text-muted-foreground">
                      {r.migratedCount}/{r.originalCount}
                    </span>
                    {r.status === "success" && <CheckCircle className="w-4 h-4 text-green-500" />}
                    {r.status === "partial" && <RefreshCw className="w-4 h-4 text-yellow-500" />}
                    {r.status === "failed" && <XCircle className="w-4 h-4 text-destructive" />}
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default AdminImageMigration;
